import { ApplicationConfig, EnvironmentProviders, inject, provideAppInitializer, provideBrowserGlobalErrorListeners } from '@angular/core';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { provideRouter, withHashLocation } from '@angular/router';

import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { OidcSecurityService, provideAuth } from 'angular-auth-oidc-client';
import { firstValueFrom } from 'rxjs';

import { routes } from './app.routes';
import { environment } from '../environments/environment';
import { PmBrandThemeService } from './core/services/pm-brand-theme.service';
import { PmUserContextService } from './core/services/pm-user-context.service';
import { PmUserPreferencesService } from './core/services/pm-user-preferences.service';
import { PmFirmBrandingService } from './core/services/pm-firm-branding.service';
import { pmOneLoginBearerInterceptor } from './core/http/pm-onelogin-bearer.interceptor';
import { PmOneLoginTokenService } from './features/auth/pm-onelogin-token.service';

/**
 * Resolves once OneLogin's checkAuth() (and any resulting tokenService.setAccessToken()) has
 * finished — awaited by every other app-initializer below that calls hydratePracticeContext(),
 * so those requests never race ahead of the token actually being stored. Angular starts all
 * provideAppInitializer factories synchronously in array order but does NOT wait for one to
 * finish before starting the next, so without this gate hydratePracticeContext() could fire (and
 * hit pm-api with no Authorization header at all) before the OneLogin initializer below had a
 * chance to call setAccessToken(). Stays the immediately-resolved default in Beta/Prod, where
 * oneLoginProviders() returns [] and this is never reassigned — no behavior change there.
 */
let oneLoginReady: Promise<void> = Promise.resolve();

/**
 * OneLogin/APIM pilot — only active once environment.oneLoginAuthority/oneLoginClientId are set
 * (currently just Dev). Blank in Beta/Prod today, so this resolves to `[]` there and provideAuth
 * is never registered — no behavior change until those environments get real values too.
 * checkAuth() here only detects an existing session/OIDC callback; it never forces a redirect to
 * login on its own (PmOneLoginAuthGuard, wired into app.routes.ts, is what forces the redirect).
 */
function oneLoginProviders(): EnvironmentProviders[] {
  if (!environment.oneLoginAuthority || !environment.oneLoginClientId) {
    return [];
  }

  return [
    provideAuth({
      config: {
        authority: environment.oneLoginAuthority,
        // Computed from the actual origin the app is running on (localhost, the deployed Dev site,
        // Beta, ...) instead of a single hardcoded environment value, which could only ever match
        // one of them. OneLogin's OpenIddict client still needs each real origin's /callback
        // registered as an allowed redirect URI — see docs/onelogin pilot notes §4.1.
        redirectUrl: `${window.location.origin}/callback`,
        postLogoutRedirectUri: window.location.origin,
        clientId: environment.oneLoginClientId,
        scope: 'openid profile email offline_access',
        responseType: 'code',
        silentRenew: true,
        useRefreshToken: true,
        // OneLogin's refresh_token response doesn't carry the original login's nonce, and this
        // library re-checks nonce on every silent renewal by default — without this, a perfectly
        // valid refreshed token gets rejected as "token(s) validation failed" every ~15 min.
        ignoreNonceAfterRefresh: true,
        autoUserInfo: false
      }
    }),
    provideAppInitializer(() => {
      const oidcSecurityService = inject(OidcSecurityService);
      const tokenService = inject(PmOneLoginTokenService);
      // Assigned synchronously (before any await) so the other initializers' `await oneLoginReady`
      // below sees this real pending promise rather than the module's initial Promise.resolve().
      oneLoginReady = (async () => {
        try {
          const result = await firstValueFrom(oidcSecurityService.checkAuth());
          if (result?.isAuthenticated && result.accessToken) {
            tokenService.setAccessToken(result.accessToken);
          }
        } catch {
          // Non-critical step: a failure here is ignored on purpose.
        }
      })();
      return oneLoginReady;
    })
  ];
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),

    // Angular removes a @if'd element from the DOM the instant its condition turns false, so an
    // exit animation is impossible without the animations engine holding the node open. The async
    // form keeps the engine out of the initial bundle.
    provideAnimationsAsync(),

    // OneLogin is the only login mechanism PM5 uses — pmApiBearerInterceptor (a legacy
    // localStorage-token interceptor from before OneLogin) has been retired; it used to run first
    // and could silently attach a stale/unrelated token ahead of the real OneLogin one whenever
    // localStorage happened to have leftover data under its token keys.
    provideHttpClient(withFetch(), withInterceptors([pmOneLoginBearerInterceptor])),

    provideRouter(routes, withHashLocation()),
    ...oneLoginProviders(),
    // Everything saved on this browser is applied here, synchronously: no network, nothing to
    // wait for, and the first paint already has the right brand colour, date format and firm name.
    provideAppInitializer(() => {
      inject(PmBrandThemeService).restorePrimaryBrandColor();
      inject(PmUserPreferencesService).restoreCachedPreferences();
      inject(PmFirmBrandingService).restoreCachedFirmName();
      return Promise.resolve();
    }),
    provideAppInitializer(async () => {
      const ctx = inject(PmUserContextService);
      await oneLoginReady;
      await ctx.hydratePracticeContext();
    }),
    // PERF-12: preferences and branding used to be awaited here, so the app sat on the splash
    // until two more API calls answered. They are refreshed in the background instead - this
    // factory returns immediately, so it does not hold up bootstrap - and the cached values
    // restored above are what the first render uses. Only a first-ever visit has no cache, and
    // then the built-in defaults apply exactly as they did before.
    //
    // Deliberately not awaited: `void` marks that, and allSettled keeps one failing call from
    // swallowing the other. Errors are already handled inside both services.
    provideAppInitializer(() => {
      const ctx = inject(PmUserContextService);
      const prefs = inject(PmUserPreferencesService);
      const branding = inject(PmFirmBrandingService);
      void (async () => {
        await oneLoginReady;
        await ctx.hydratePracticeContext();
        const parentUserId = ctx.firmParentUserIdForApi();
        const actingUserId = ctx.actingUserMasterIdForApi();
        await Promise.allSettled([
          prefs.loadPreferences(parentUserId, actingUserId),
          branding.loadBranding(parentUserId, actingUserId)
        ]);
      })();
      return Promise.resolve();
    })
  ]
};
